import React, { useMemo } from 'react'
import { usePaletteTheme } from '../../context/paletteTheme'
import { Group } from '@visx/group'
import { Treemap, hierarchy, stratify, treemapResquarify } from '@visx/hierarchy'
import { withParentSize } from '@visx/responsive'
import { Text } from '@visx/text'
import { scaleLinear } from '@visx/scale'
import { extent } from 'd3-array'
import { useTooltip } from "@visx/tooltip"
import { getValue } from './graphUtils'
import TreemapGraphTooltip from './TreemapGraphTooltip'
import { numberFormatter } from '../../utils/tools'
import { darkColors } from '../../utils/darkPalette'

const addParents = (data) => {
  const parentsSet = data.reduce((acc,cur) => acc.add(cur.group), new Set() )
  const parentsArray = Array.from(parentsSet).map(item => ({ group: 'root', label: item, value: null }))
  const root = {group: null, label: 'root', value: null}
  return [root, ...parentsArray, ...data]
}

function TreemapGraph({
  parentWidth: width,
  parentHeight: height,
  margin = { top: 0, left: 0, right: 0, bottom: 0 },
  data,
  valueFormat,
  datasetLabel,
  timeFrameLabel,
  onClick,
}) {
  const tooltip = useTooltip()
  const { hideTooltip, showTooltip } = tooltip
  const { colors } = usePaletteTheme()

  const xMax = width - margin.left - margin.right
  const yMax = height - margin.top - margin.bottom
  
  const formatNumber = numberFormatter(valueFormat)
  const withParents = useMemo(() => addParents(data),[data])

  const orginizedData = stratify()
    .id(d => d.label)
    .parentId(d => d.group)(withParents)
    .sum(d => Math.abs(d.value) || 0)
  const root = hierarchy(orginizedData).sort((a, b) => (Math.abs(b.value) || 0) - (Math.abs(a.value) || 0))

  const colorScales = useMemo(() => {
    const extents = extent(data, getValue)
    const { stockUp, stockDown } = colors
    return [
      scaleLinear({
        domain: [0, extents[1]],
        range: [darkColors.dark10, stockUp],
      }),
      scaleLinear({
        domain: [extents[0], 0],
        range: [stockDown, darkColors.dark10],
      }),
    ]
  },[data, colors])

  return width < 10 ? null : (
    <svg width={width} height={height}>
      <rect width={width} height={height} />
      <Treemap
        top={margin.top}
        root={root}
        size={[xMax, yMax]}
        tile={treemapResquarify}
        paddingTop={(node) => node.depth === 1 ? 20 : 0}
        round
      >
        {treemap => (
          <Group>
            {treemap
              .descendants()
              .map((node, i) => {
                const nodeWidth = node.x1 - node.x0
                const nodeHeight = node.y1 - node.y0
                return (
                  <Group
                    key={`node-${i}`}
                    top={node.y0 + margin.top}
                    left={node.x0 + margin.left}
                    onMouseMove={(e) => node.depth > 1 && showTooltip({
                      tooltipLeft: e?.clientX || 0,
                      tooltipTop: e?.clientY || 0,
                      tooltipData: { 
                        ticker: node.data.id,
                        value: (node.data.data.value)/100,
                        name:(node.data.data.security_name),
                        valueFormat,
                        datasetLabel,
                        timeFrameLabel,
                      }
                    })}
                    onMouseLeave={() => hideTooltip()}
                    onClick={() => node.depth > 1 && onClick(node.data.id)}
                    style={{cursor: 'pointer'}}
                  >
                    {node.depth === 1 && (
                      <>
                        <rect
                          width={nodeWidth}
                          height={nodeHeight}
                          fill={colors.treemapBackground}
                        />
                        <Text
                          x={nodeWidth / 2}
                          y={0}
                          dy={5}
                          width={nodeWidth}
                          height={nodeHeight}
                          verticalAnchor="start"
                          textAnchor="middle"
                          fill={colors.textPrimary}
                        >
                          {node.data.id}
                        </Text>
                      </>
                    )}
                    {node.depth === 2 && (
                      <>
                        <rect
                          width={nodeWidth}
                          height={nodeHeight}
                          fill={colorScales[node.data.data.value > 0 ? 0 : 1](node.data.data.value || 0) }
                        />
                        <Text
                          x={nodeWidth / 2}
                          y={nodeHeight / 2}
                          width={nodeWidth}
                          height={nodeHeight}
                          verticalAnchor="middle"
                          textAnchor="middle"
                          fill={darkColors.dark2}
                        >
                          {`${nodeWidth > 130 ?node.data.data.security_name : node.data.data.security_name.substring(0,2)+'..'} ${nodeWidth > 30 ? formatNumber(node.data.data.value/100) : ''}`}
                        </Text>
                      </>
                    )}
                  </Group>
                )
              })}
          </Group>
        )}
      </Treemap>
      <TreemapGraphTooltip tooltip={tooltip} width={width} height={height} margin={margin} />
    </svg>
  )
}

export default withParentSize(TreemapGraph)